import { useCallback, useEffect, useState } from "react";
import { ChatCircleText, Tray } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";

const LABELS = {
  unclassified: "Unclassified",
  obligation: "Obligation",
  update: "Update",
  noise: "Noise",
};

const BADGES = {
  unclassified: "bg-secondary text-muted-foreground",
  obligation: "bg-destructive/10 text-destructive",
  update: "bg-success/10 text-success",
  noise: "bg-muted text-muted-foreground",
};

const RECLASSIFY_LABELS = ["obligation", "update", "noise"];

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const counterpartyLabel = (item) =>
  item.counterparty_name || item.counterparty_identity || "Unknown counterparty";

export default function CommsAudit() {
  const { hid } = useHousehold();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    if (!hid) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return Promise.resolve();
    }

    setLoading(true);
    return api.get("/comms/audit", { params: { household_id: hid, limit: 50, offset: 0 } })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
        toast.error("Could not load communications audit");
      })
      .finally(() => setLoading(false));
  }, [hid]);

  useEffect(() => { load(); }, [load]);

  const reclassify = async (item, classification) => {
    if (!hid || item.classification === classification) return;
    setBusyId(item.id);
    try {
      await api.post(`/comms/audit/${item.id}/reclassify`, {
        household_id: hid,
        classification,
        reason: "Operator reclassified from audit surface",
      });
      toast.success("Classification updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reclassify");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Communications</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Comms Audit</h1>
        </div>
        <div className="text-sm text-muted-foreground">
          {loading ? "Loading..." : `${total} message${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {items.length === 0 ? (
        <div data-testid="audit-empty" className="bg-card border border-border rounded-sm p-12 text-center">
          <Tray size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-display text-xl font-bold">No communications yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Ingested communications will appear here with their classification and reason.
          </p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-bold p-4">Communication</th>
                  <th className="text-left font-bold p-4 hidden md:table-cell">Counterparty</th>
                  <th className="text-left font-bold p-4">Classification</th>
                  <th className="text-left font-bold p-4 hidden lg:table-cell">Reason</th>
                  <th className="text-right font-bold p-4">Reclassify</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-testid="audit-row" className="border-b border-border last:border-0 hover:bg-secondary">
                    <td className="p-4 min-w-[240px]">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 bg-secondary flex items-center justify-center rounded-sm shrink-0">
                          <ChatCircleText size={20} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{item.subject || "(No subject)"}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {item.channel || "message"} / {fmtDate(item.sent_at)}
                          </p>
                          <p className="text-xs text-muted-foreground md:hidden truncate">{counterpartyLabel(item)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground min-w-[180px]">
                      <span className="block truncate">{counterpartyLabel(item)}</span>
                    </td>
                    <td className="p-4">
                      <span
                        data-testid="audit-classification"
                        className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm whitespace-nowrap ${BADGES[item.classification] || BADGES.unclassified}`}
                      >
                        {LABELS[item.classification] || item.classification}
                      </span>
                    </td>
                    <td className="p-4 hidden lg:table-cell text-muted-foreground max-w-md">
                      <span data-testid="audit-reason" className="line-clamp-2">
                        {item.classification_reason || "-"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        {RECLASSIFY_LABELS.map((label) => (
                          <Button
                            key={label}
                            type="button"
                            size="sm"
                            variant={item.classification === label ? "secondary" : "outline"}
                            disabled={busyId === item.id || item.classification === label}
                            data-testid={`audit-reclassify-${label}`}
                            onClick={() => reclassify(item, label)}
                            className="rounded-sm font-semibold"
                          >
                            {LABELS[label]}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
